import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Pagination, Response } from '@ui/src';
import { TopUpInstructions, TopUpVisibility } from '../../types';
import { customersComponentApi } from '../../../customers/store/services/customers';

export interface TopUpPanelState {
  topUpInstrusctions?: TopUpInstructions;
  topUpOptions?: {
    companyName: string;
    id: string;
    visibleToClients: boolean;
  }[];
  topUpOptionsPagination?: Pagination;
  topUpVisibility?: TopUpVisibility[];
  topUpIds?: string[];
  topUpIsLoading: boolean;
}

const initialState: TopUpPanelState = {
  topUpIsLoading: true,
};

export const topUpPanelSlice = createSlice({
  name: 'topUpPanel',
  initialState,
  reducers: {
    setTopUpIds: (state, action: PayloadAction<string[]>) => {
      state.topUpIds = action.payload;
    },
    clearTopUpInstruction: (state) => {
      state.topUpInstrusctions = undefined;
    },
    clearTopUpOptions: (state) => {
      state.topUpOptions = undefined;
      state.topUpOptionsPagination = undefined;
      state.topUpVisibility = undefined;
      state.topUpIds = [];
      state.topUpIsLoading = true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersTopUpInstructions
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TopUpInstructions }>) => {
        state.topUpInstrusctions = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getAllTopUpVisibility.matchFulfilled,
      (state, action: PayloadAction<Response<TopUpVisibility>>) => {
        let nextTopUpVisibility: TopUpVisibility[] = [];

        if (state.topUpVisibility?.length) {
          nextTopUpVisibility = ([] as TopUpVisibility[]).concat(
            state.topUpVisibility,
          );
        }

        action?.payload?.data?.edges?.forEach((item) => {
          nextTopUpVisibility.push(item.node);
        });

        state.topUpVisibility = nextTopUpVisibility;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersTopUp.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            id: string;
            companyName: string;
            visibleToClients: boolean;
          }>
        > & {
          meta: {
            arg: {
              originalArgs: { cursor?: string; companyName?: string };
            };
          };
        },
      ) => {
        if (!state.topUpOptions) {
          state.topUpOptions = action.payload.data.edges.map(
            (item) => item.node,
          );
        } else if (
          state.topUpOptions &&
          action?.meta?.arg?.originalArgs?.cursor
        ) {
          const nextTopUpOptions = (
            [] as {
              id: string;
              companyName: string;
              visibleToClients: boolean;
            }[]
          ).concat(state.topUpOptions);

          action.payload.data.edges.forEach((item) => {
            if (
              !nextTopUpOptions.find((option) => option.id === item.node.id)
            ) {
              nextTopUpOptions.push(item.node);
            }
          });

          state.topUpOptions = nextTopUpOptions;
        }

        if (!action.meta.arg.originalArgs.companyName) {
          state.topUpOptionsPagination = action.payload.data.pageInfo;
        }

        state.topUpIsLoading = false;
      },
    );
  },
});

export const { clearTopUpInstruction, clearTopUpOptions, setTopUpIds } =
  topUpPanelSlice.actions;

export default topUpPanelSlice.reducer;
